import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Layout, Card, Button, Input, Select, Tag, Space, Typography, Tabs,
  List, Avatar, Badge, Drawer, Form, message, Spin, Alert, Row, Col,
  Divider, Tooltip, Modal, Rate, Progress, Empty, Segmented,
} from 'antd';
import {
  SendOutlined, FireOutlined, UserOutlined, HomeOutlined, CalendarOutlined,
  WhatsAppOutlined, PhoneOutlined, CopyOutlined, RobotOutlined, SearchOutlined,
  PlusOutlined, EditOutlined, CheckCircleOutlined, ClockCircleOutlined,
  EnvironmentOutlined, DollarOutlined, StarOutlined, ThunderboltOutlined,
  ReloadOutlined, EyeOutlined,
} from '@ant-design/icons';

const { Sider, Content } = Layout;
const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const API = '/api/v1/jj';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

const TIER_COLOR = { hot: '#ff4d4f', warm: '#fa8c16', cold: '#1890ff' };
const TIER_BG = { hot: '#fff1f0', warm: '#fff7e6', cold: '#e6f7ff' };

const SOURCE_ICONS = {
  line: '💬', whatsapp: '📱', wechat: '💚', facebook: '👤',
  ddproperty: '🏢', livinginsider: '🏠', website: '🌐', manual: '✏️',
};

// ── Lead Scorer UI ─────────────────────────────────────────────────────────────
function LeadScoreBar({ score, breakdown }) {
  const { t } = useTranslation();
  const tier = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold';
  const labels = { intentClarity: t('inbox.intent','Intent'), budgetMatch: t('inbox.budget','Budget'), areaMatch: t('inbox.area','Area'), urgency: t('inbox.urgency','Urgency') };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: TIER_COLOR[tier], color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700,
        }}>{score}</div>
        <div>
          <Tag color={TIER_COLOR[tier]} style={{ fontWeight: 700, fontSize: 13 }}>
            {tier === 'hot' ? '🔥 HOT' : tier === 'warm' ? '⭐ WARM' : '❄️ COLD'}
          </Tag>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Lead Score / 100</div>
        </div>
      </div>
      {breakdown && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {Object.entries(breakdown).map(([key, val]) => (
            <div key={key}>
              <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>{labels[key] || key} {val}/25</div>
              <Progress percent={(val / 25) * 100} size="small" showInfo={false}
                strokeColor={val >= 20 ? '#52c41a' : val >= 10 ? '#fa8c16' : '#ff4d4f'} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Property Card ─────────────────────────────────────────────────────────────
function PropertyCard({ prop, onSend, onSchedule }) {
  const { t } = useTranslation();
  const p = prop.parsed || {};
  const price = p.price || {};
  const loc = p.location || {};
  // Support both fbImages (obj with localPath) and images (full URL array from Serina API)
  const fbImgs = prop.fbImages || [];
  const urlImgs = prop.images || (prop.parsed && prop.parsed.images) || [];
  const fbImg = fbImgs.find(i => i.width >= 100)?.localPath;
  const urlImg = urlImgs.find(u => u && !u.includes('pixel-lazyLoad')) || urlImgs[0];
  const imgSrc = fbImg
    ? `http://10.66.99.28:18810/images${fbImg}`
    : (urlImg && !urlImg.startsWith('http')) ? `http://10.66.99.28:18810/images${urlImg}` : urlImg;

  return (
    <Card
      size="small"
      style={{ marginBottom: 8, borderRadius: 8 }}
      cover={imgSrc ? (
        <img src={imgSrc} alt="" style={{ height: 120, objectFit: 'cover', borderRadius: '8px 8px 0 0' }}
          onError={e => { e.target.style.display = 'none'; }} />
      ) : null}
      actions={[
        <Button size="small" icon={<WhatsAppOutlined />} onClick={() => onSend(prop)}>{t('inbox.sendToCustomer','Send to Customer')}</Button>,
        <Button size="small" icon={<CalendarOutlined />} type="primary" ghost onClick={() => onSchedule && onSchedule(prop)} style={{ borderColor: '#52c41a', color: '#52c41a' }}>{t('inbox.viewHouse','View House')}</Button>,
        <Button size="small" icon={<EyeOutlined />} href={prop.fbPostUrl} target="_blank">{t('inbox.fbOriginal','FB Post')}</Button>,
      ]}
    >
      <div>
        <Text strong style={{ fontSize: 13 }}>{p.title || 'Condo'}</Text>
        <div style={{ marginTop: 4 }}>
          <Tag color="green">{price.amount?.toLocaleString()} {price.currency}/{price.period === 'monthly' ? 'mo' : price.period}</Tag>
          <Tag color="blue">{p.propertyType}</Tag>
        </div>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          <EnvironmentOutlined /> {loc.area} · BTS {loc.nearBTS}
        </div>
        {p.bedrooms && <div style={{ fontSize: 12, color: '#666' }}>🛏 {p.bedrooms} bed · 🛁 {p.bathrooms} bath</div>}
      </div>
    </Card>
  );
}

// ── Ad Posts Performance Panel ────────────────────────────────────────────────
function AdPostsPanel() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const API = '/api/v1/jj';
  const h = () => ({ Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`, 'Content-Type': 'application/json' });

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/ad-posts?limit=50`, { headers: h() }).then(r => r.json());
      setPosts(res.posts || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const channelColors = {
    facebook: '#1877f2', instagram: '#e4405f', youtube: '#ff0000',
    ddproperty: '#ff6b00', livinginsider: '#00b900', website: '#722ed1',
  };

  return (
    <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('adPosts.subtitle', 'Track which ad posts bring the most leads')}
        </Text>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadPosts} loading={loading} />
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
      ) : posts.length === 0 ? (
        <Empty description={t('adPosts.noPosts', 'No ad posts yet')} />
      ) : (
        posts.map((post, i) => (
          <Card key={post.id} size="small" style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <Space>
                  <Tag color={channelColors[post.channel] || 'default'}>{post.channel}</Tag>
                  <Text strong style={{ fontSize: 13 }}>{post.title || t('adPosts.untitled', 'Untitled')}</Text>
                </Space>
                {post.url && (
                  <div style={{ marginTop: 2 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>{post.url?.substring(0, 60)}...</Text>
                  </div>
                )}
                {post.published_at && (
                  <div><Text type="secondary" style={{ fontSize: 11 }}>📅 {new Date(post.published_at).toLocaleDateString()}</Text></div>
                )}
              </div>
              <div style={{ textAlign: 'center', minWidth: 60 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#722ed1' }}>{post.lead_count}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>{t('adPosts.leads', 'leads')}</Text>
              </div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function JJInboxPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [inquiries, setInquiries] = useState([]);
  const [followupQueue, setFollowupQueue] = useState([]);
  const [selected, setSelected] = useState(null);   // selected inquiry
  const [loadingProps, setLoadingProps] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState('inbox');
  const [tierFilter, setTierFilter] = useState('all');
  const [newInquiryText, setNewInquiryText] = useState('');
  const [newInquirySource, setNewInquirySource] = useState('manual');
  const [copySuccess, setCopySuccess] = useState(false);
  const [rightTab, setRightTab] = useState('chat');   // chat | analysis | properties
  const [scheduleModal, setScheduleModal] = useState(null); // property to schedule viewing for
  const [schedulingProp, setSchedulingProp] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [conversation, setConversation] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [customerViewings, setCustomerViewings] = useState([]);
  const [updatingViewing, setUpdatingViewing] = useState(null); // vid being updated
  const chatEndRef = useRef(null);
  const autoSelectDone = useRef(false);

  const loadInquiries = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const tier = tierFilter !== 'all' ? `&tier=${tierFilter}` : '';
      const [inqRes, fuRes] = await Promise.all([
        fetch(`${API}/inquiries?limit=100${tier}`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/followups/queue`, { headers: h() }).then(r => r.json()),
      ]);
      const inqs = inqRes.inquiries || [];
      setInquiries(inqs);
      setFollowupQueue(fuRes.queue || []);

      // Auto-select customer from URL param (?customer=ID from CRM page)
      const paramCustomerId = searchParams.get('customer');
      if (paramCustomerId && !autoSelectDone.current) {
        autoSelectDone.current = true;
        const target = inqs.find(i => String(i.customer?.id) === String(paramCustomerId));
        if (target) {
          // Existing inquiry found — select it
          setSelected({ ...target, matched_properties: [] });
          setLoadingProps(true);
          fetch(`${API}/customers/${paramCustomerId}`, { headers: h() })
            .then(r => r.json())
            .then(detail => {
              const props = detail.matched_properties || [];
              setSelected(prev => ({ ...prev, customer: detail, matched_properties: props }));
              if (props.length > 0) setRightTab('properties');
            })
            .finally(() => setLoadingProps(false));
        } else {
          // No inquiry yet — load customer directly and open new conversation UI
          setLoadingProps(true);
          fetch(`${API}/customers/${paramCustomerId}`, { headers: h() })
            .then(r => r.json())
            .then(detail => {
              if (detail && detail.id) {
                setSelected({
                  id: null,
                  source: detail.source || 'manual',
                  original_text: '',
                  status: 'new',
                  is_hot: false,
                  customer: detail,
                  matched_properties: detail.matched_properties || [],
                  classification: {
                    leadScore: detail.lead_score || 0,
                    tier: detail.tier || 'cold',
                    autoReply: null,
                  },
                  _newCustomer: true,   // flag: no prior inquiries
                });
              }
            })
            .finally(() => setLoadingProps(false));
        }
      }
    } finally {
      setLoading(false);
    }
  }, [tierFilter, searchParams]);

  useEffect(() => { loadInquiries(); }, [loadInquiries]);

  // Auto-poll new inquiries every 8 seconds — background refresh without flicker
  useEffect(() => {
    const interval = setInterval(() => {
      loadInquiries(true);
    }, 8000);
    return () => clearInterval(interval);
  }, [loadInquiries]);

  const handleSubmitInquiry = async () => {
    if (!newInquiryText.trim()) return message.warning(t('inbox.enterInquiry','Please enter inquiry content'));
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: 'POST',
        headers: h(),
        body: JSON.stringify({ source: newInquirySource, original_text: newInquiryText }),
      });
      const data = await res.json();
      if (data.ok) {
        message.success(`${t('inbox.analysisComplete','Analysis done')} | Tier: ${data.tier?.toUpperCase()} | Score: ${data.classification?.leadScore}`);
        setNewInquiryText('');
        setSelected(data);
        loadInquiries(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyReply = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
    message.success(t('inbox.replyCopied','Auto-reply copied! Paste into Line / WhatsApp'));
  };

  const handleMarkReplied = async (id) => {
    await fetch(`${API}/inquiries/${id}/mark-replied`, { method: 'POST', headers: h() });
    message.success(t('inbox.markedReplied','Marked as replied'));
    loadInquiries(true);
  };

  const handleSendFollowup = async (fuId, msg) => {
    await fetch(`${API}/followups/${fuId}/send`, {
      method: 'POST', headers: h(), body: JSON.stringify({ message: msg }),
    });
    message.success(t('inbox.followupSent','Follow-up marked as sent'));
    loadInquiries(true);
  };

  // Load conversation when customer is selected
  const loadConversation = useCallback(async (customerId) => {
    if (!customerId) return;
    const data = await fetch(`${API}/customers/${customerId}/conversation`, { headers: h() }).then(r => r.json());
    setConversation(data);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, []);

  useEffect(() => {
    if (selected?.customer?.id) {
      loadConversation(selected.customer.id);
      setRightTab('chat');
    }
  }, [selected?.customer?.id, loadConversation]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages?.length]);

  // Load viewings when customer is selected
  useEffect(() => {
    if (selected?.customer?.id) {
      loadCustomerViewings(selected.customer.id);
    } else {
      setCustomerViewings([]);
    }
  }, [selected?.customer?.id]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSendingReply(true);
    try {
      const latestInqId = selected.id || selected.inquiry_id;

      if (latestInqId) {
        // Existing inquiry — save agent reply
        await fetch(`${API}/inquiries/${latestInqId}/reply`, {
          method: 'POST', headers: h(), body: JSON.stringify({ reply: replyText }),
        });
      } else if (selected._newCustomer && selected.customer?.id) {
        // New customer with no prior inquiry — create a manual outbound inquiry record
        await fetch(`${API}/inquiries`, {
          method: 'POST', headers: h(),
          body: JSON.stringify({
            source: selected.customer.source || 'manual',
            original_text: `[Agent outbound] ${replyText}`,
            customer_id: selected.customer.id,
          }),
        });
        // Then save the reply on the newly created inquiry
        const inqRes = await fetch(`${API}/inquiries?limit=1`, { headers: h() }).then(r => r.json());
        const newestInq = (inqRes.inquiries || [])[0];
        if (newestInq) {
          await fetch(`${API}/inquiries/${newestInq.id}/reply`, {
            method: 'POST', headers: h(), body: JSON.stringify({ reply: replyText }),
          });
        }
        // Reload so this customer now appears in the inbox list
        await loadInquiries(true);
      }

      message.success(t('inbox.savedCopy','Saved! Please copy and send via Line / WhatsApp'));
      navigator.clipboard.writeText(replyText);
      setReplyText('');
      loadConversation(selected.customer?.id);
      if (!selected._newCustomer) loadInquiries(true);
    } finally {
      setSendingReply(false);
    }
  };

  const handleGenerateAIReply = async () => {
    setGeneratingAI(true);
    try {
      // Use the auto_reply from classification if available
      const autoReply = selected?.classification?.autoReply || selected?.auto_reply;
      if (autoReply) {
        setReplyText(autoReply);
      } else if (selected?.customer?.id) {
        // Generate fresh reply via customer generate endpoint
        const res = await fetch(`${API}/customers/${selected.customer.id}/generate-reply`, {
          method: 'POST', headers: h(),
        }).then(r => r.json());
        const firstMsg = res.messages?.[0];
        if (firstMsg?.message) setReplyText(firstMsg.message);
      }
    } finally {
      setGeneratingAI(false);
    }
  };

  // Group inquiries by customer so the same LINE ID / contact shows as ONE row
  const groupedInquiries = useMemo(() => {
    const isSystemMsg = (t) => {
      const s = (t || '').trim();
      return s.startsWith('[Agent') || s.startsWith('[Bot') || s.startsWith('[Auto Property Match]');
    };
    const map = new Map();
    for (const inq of inquiries) {
      const cid = inq.customer?.id ?? inq.customer_id ?? `raw-${inq.id}`;
      const existing = map.get(cid);
      const isSys = isSystemMsg(inq.original_text);
      if (!existing) {
        map.set(cid, {
          ...inq,
          _msgCount: isSys ? 0 : 1,
          _latestAt: new Date(inq.created_at || 0).getTime(),
          // representative row text: prefer latest real customer message
          _rep: isSys ? null : inq,
        });
      } else {
        if (!isSys) existing._msgCount += 1;
        const ts = new Date(inq.created_at || 0).getTime();
        if (ts > existing._latestAt) {
          existing._latestAt = ts;
          existing._latest = inq;
        }
        if (!isSys) {
          if (!existing._rep || ts > new Date(existing._rep.created_at || 0).getTime()) {
            existing._rep = inq;
          }
        }
      }
    }
    return Array.from(map.values()).map(item => {
      const rep = item._rep || item._latest || item;
      // carry over the customer object so tier / name / score render correctly
      return { ...rep, _msgCount: item._msgCount, _customer: item.customer };
    });
  }, [inquiries]);

  const tierCounts = {
    all: groupedInquiries.length,
    hot: groupedInquiries.filter(i => i.customer?.tier === 'hot').length,
    warm: groupedInquiries.filter(i => i.customer?.tier === 'warm').length,
    cold: groupedInquiries.filter(i => i.customer?.tier === 'cold').length,
  };

  const handleSerinaMatch = async () => {
    // Get the latest real customer message as the search text
    const msgs = conversation?.messages || [];
    const isSystemMsg = (m) => {
      const t = (m?.content || m?.message || '').trim();
      return t.startsWith('[Bot') || t.startsWith('[Auto Property Match]') || t.startsWith('[Agent');
    };
    const lastCustomerMsg = [...msgs].reverse().find(m => (m.role === 'customer' || m.direction === 'in') && !isSystemMsg(m));
    const searchText = lastCustomerMsg?.content || lastCustomerMsg?.message ||
      selected?.raw_text || selected?.classification?.intentSummary || '';

    if (!searchText || isSystemMsg({ content: searchText })) {
      message.warning('No customer message to search from');
      return;
    }

    setGeneratingAI(true);
    try {
      const res = await fetch(`${API}/house/serina/match`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ text: searchText, customer_id: selected?.customer?.id }),
      }).then(r => r.json());

      if (res.serina_reply) {
        setReplyText(res.serina_reply);
        setRightTab('chat');
        // Also update matched properties panel
        if (res.properties?.length > 0) {
          setSelected(prev => ({ ...prev, matched_properties: res.properties }));
        }
        message.success(`🏠 Serina found ${res.matched_count} matching properties`);
      } else {
        message.warning('No properties matched the requirements');
      }
    } catch (e) {
      message.error('Serina match failed: ' + e.message);
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleScheduleViewing = async (prop) => {
    const title = prop.parsed?.title || 'Property';
    setSchedulingProp(title);
    setScheduleDate('');
    setScheduleModal(prop);
  };

  const confirmScheduleViewing = async () => {
    if (!selected?.customer?.id) return;
    const title = schedulingProp || scheduleModal?.parsed?.title || 'Property';
    try {
      await fetch(`${API}/viewings`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({
          customer_id: selected.customer.id,
          listing_title: title,
          scheduled_at: scheduleDate || undefined,
        }),
      });
      message.success(`📅 ${t('inbox.viewingArranged','Viewing arranged!')} — ${title}`);
      // Also copy a viewing confirmation message
      const msg = `Hi ${selected.customer?.name || 'there'}! 😊\n\nI've arranged a viewing for:\n🏠 ${title}\n📅 ${scheduleDate ? new Date(scheduleDate).toLocaleString() : 'TBC'}\n\nI'll confirm the exact time shortly. Looking forward to showing you the property!`;
      setReplyText(msg);
      setRightTab('chat');
      setScheduleModal(null);
      await loadCustomerViewings(selected.customer.id);
    } catch (e) {
      message.error(t('inbox.viewingFailed', 'Failed to arrange viewing'));
    }
  };

  // ── Viewing Result ──
  const loadCustomerViewings = async (customerId) => {
    if (!customerId) return;
    try {
      const res = await fetch(`${API}/viewings?customer_id=${customerId}`, { headers: h() }).then(r => r.json());
      setCustomerViewings(res.viewings || []);
    } catch { /* silently fail */ }
  };

  const handleUpdateViewingResult = async (vid, result) => {
    setUpdatingViewing(vid);
    try {
      const res = await fetch(`${API}/viewings/${vid}/result`, {
        method: 'PATCH', headers: h(),
        body: JSON.stringify({ result }),
      });
      const data = await res.json();
      if (data.ok) {
        message.success(t('inbox.viewingResultUpdated', 'Viewing result updated'));
        await loadCustomerViewings(selected?.customer?.id);
        if (result === 'no') {
          // Prompt to re-match properties (flowchart "No → back to Find Listing")
          Modal.confirm({
            title: t('inbox.viewingRematchTitle', 'Customer not satisfied'),
            icon: <SearchOutlined />,
            content: t('inbox.viewingRematchContent', 'Would you like to search for new matching properties?'),
            okText: t('inbox.viewingRematchYes', 'Yes, find new properties'),
            cancelText: t('common.cancel', 'Cancel'),
            onOk: () => {
              setRightTab('properties');
              // Trigger Serina match using customer preferences
              if (selected?.customer?.id) {
                handleSerinaMatchForCustomer(selected.customer.id);
              }
            },
          });
        }
      }
    } catch (e) {
      message.error(t('inbox.viewingResultFailed', 'Failed to update viewing result'));
    } finally {
      setUpdatingViewing(null);
    }
  };

  const handleSerinaMatchForCustomer = async (customerId) => {
    setGeneratingAI(true);
    try {
      const res = await fetch(`${API}/house/serina/match`, {
        method: 'POST', headers: h(),
        body: JSON.stringify({ text: selected?.customer?.preferred_area || '', customer_id: customerId }),
      }).then(r => r.json());
      if (res.properties?.length > 0) {
        setSelected(prev => ({ ...prev, matched_properties: res.properties }));
        message.success(`🏠 Serina found ${res.matched_count} matching properties`);
      } else {
        message.info(t('inbox.noProperties', 'No matched properties'));
      }
    } finally {
      setGeneratingAI(false);
    }
  };

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', background: '#f0f2f5' }}>
      {/* ── Left: Inquiry List ── */}
      <Sider width={360} style={{ background: '#fff', borderRight: '1px solid #f0f0f0', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Title level={5} style={{ margin: 0 }}>📨 {t('jj.inboxTitle', 'JJ Unified Inbox')}</Title>
            <Button size="small" icon={<ReloadOutlined />} onClick={loadInquiries} />
          </div>
          <Segmented
            size="small"
            value={tierFilter}
            onChange={setTierFilter}
            options={[
              { label: `${t('inbox.all','All')} ${tierCounts.all}`, value: 'all' },
              { label: `🔥 ${tierCounts.hot}`, value: 'hot' },
              { label: `⭐ ${tierCounts.warm}`, value: 'warm' },
              { label: `❄️ ${tierCounts.cold}`, value: 'cold' },
            ]}
          />
        </div>

        {/* New Inquiry Input */}
        <div style={{ padding: '12px 16px', borderBottom: '2px solid #f0f0f0', background: '#fefefe' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <Select size="small" value={newInquirySource} onChange={setNewInquirySource} style={{ width: 110 }}>
              {Object.entries(SOURCE_ICONS).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v} {k}</Select.Option>
              ))}
            </Select>
          </div>
          <TextArea
            value={newInquiryText}
            onChange={e => setNewInquiryText(e.target.value)}
            {...{placeholder: t('inbox.pasteInquiry', 'Paste customer message... (Thai / English / Chinese)')}}
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ fontSize: 13 }}
          />
          <Button
            type="primary" block icon={<RobotOutlined />}
            loading={submitting} onClick={handleSubmitInquiry}
            style={{ marginTop: 6, background: '#722ed1', borderColor: '#722ed1' }}
          >
            {t('inbox.analyzeBtn','AI Analyze + Score')}
          </Button>
        </div>

        {/* Inquiry List */}
        {loading ? <Spin style={{ display: 'block', margin: '20px auto' }} /> : (
          <div style={{ overflow: 'auto', flex: 1 }}>
            {groupedInquiries.length === 0 ? <Empty description={t('inbox.noInquiries','No inquiries')} style={{ marginTop: 40 }} /> : (
              groupedInquiries.map(inq => {
                const c = inq.customer;
                const tier = c?.tier || 'cold';
                const isActive = selected?.customer?.id === inq.customer?.id || selected?.id === inq.id;
                return (
                  <div
                    key={inq.id}
                    onClick={async () => {
                      // Set basic data immediately for fast UI response
                      setSelected({ ...inq, fromList: true, matched_properties: [] });
                      // Then fetch full customer detail including HouseAI matched properties
                      if (inq.customer?.id) {
                        setLoadingProps(true);
                        try {
                          const detail = await fetch(`${API}/customers/${inq.customer.id}`, { headers: h() }).then(r => r.json());
                          setSelected(prev => ({
                            ...prev,
                            customer: detail,
                            matched_properties: detail.matched_properties || [],
                          }));
                        } catch(e) {
                          // silently fail — properties just won't show
                        } finally {
                          setLoadingProps(false);
                        }
                      }
                    }}
                    style={{
                      padding: '10px 16px', borderBottom: '1px solid #f5f5f5',
                      background: isActive ? TIER_BG[tier] : (inq.is_hot ? '#fff7f0' : '#fff'),
                      cursor: 'pointer', borderLeft: `3px solid ${TIER_COLOR[tier]}`,
                      transition: 'background .15s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Space>
                        <Avatar size={32} style={{ background: TIER_COLOR[tier], fontSize: 12 }}>
                          {(c?.name || '?')[0]}
                        </Avatar>
                        <div>
                          <Space size={4}>
                            <Text strong style={{ fontSize: 13 }}>{c?.name || t('inbox.unknown','Unknown')}</Text>
                            {inq._msgCount > 1 && (
                              <Badge count={inq._msgCount} size="small" style={{ backgroundColor: '#722ed1' }} />
                            )}
                          </Space>
                          <div>
                            <Tag style={{ fontSize: 10, padding: '0 4px' }}>{SOURCE_ICONS[inq.source]} {inq.source}</Tag>
                            {inq.listing_title && (
                              <Tag color="geekblue" style={{ fontSize: 10, padding: '0 4px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                🏢 {inq.listing_title}
                              </Tag>
                            )}
                            {inq.is_hot && <Tag color="red" style={{ fontSize: 10, padding: '0 4px' }}>🔥 HOT</Tag>}
                            {inq.status === 'replied' && <Tag color="green" style={{ fontSize: 10, padding: '0 4px' }}>{t('inbox.replied','✅ Replied')}</Tag>}
                          </div>
                        </div>
                      </Space>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: TIER_COLOR[tier] }}>{c?.lead_score || 0}</div>
                        <Text type="secondary" style={{ fontSize: 10 }}>{t('inbox.score','pts')}</Text>
                      </div>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inq.original_text}
                    </Text>
                  </div>
                );
              })
            )}
          </div>
        )}
      </Sider>

      {/* ── Right: Detail Panel ── */}
      <Content style={{ overflow: 'auto', padding: 20 }}>
        {!selected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 16 }}>
            <RobotOutlined style={{ fontSize: 64, color: '#722ed1' }} />
            <Title level={4} type="secondary">{t('inbox.title','JJ Real Estate AI Inbox')}</Title>
            <Text type="secondary">{t('inbox.hint','Paste customer inquiry on the left — AI scores, generates reply, and matches properties')}</Text>
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* CRM back-link banner */}
            {searchParams.get('customer') && (
              <Col span={24} style={{ marginBottom: 12 }}>
                <Alert
                  message={
                    <Space>
                      <span>{t('inbox.openedFromCRM','Opened from Case Management:')}</span>
                      <Text strong>{selected.customer?.name || t('inbox.customer','Customer')}</Text>
                      {selected.customer?.line_id && <Tag color="#00b900">💬 {selected.customer.line_id}</Tag>}
                      {selected.customer?.whatsapp && <Tag color="#25d366">📱 {selected.customer.whatsapp}</Tag>}
                      <Button size="small" type="link" style={{ padding: 0 }}
                        onClick={() => window.history.back()}>{t('inbox.backToCRM','← Back to CRM')}</Button>
                    </Space>
                  }
                  type="info" showIcon={false}
                  style={{ padding: '5px 12px', background: '#e6f7ff', border: '1px solid #91d5ff' }}
                />
              </Col>
            )}

            {/* Source property banner — helps agent know which listing the customer asked about */}
            {selected.listing_title && (
              <Col span={24} style={{ marginBottom: 12 }}>
                <Alert
                  type="warning"
                  icon={<span>🏢</span>}
                  message={
                    <Space wrap>
                      <Text strong style={{ fontSize: 13 }}>{t('inbox.sourceProperty', 'Source Property')}</Text>
                      <Text style={{ fontSize: 13 }}>{selected.listing_title}</Text>
                      {selected.listing_id && (
                        <Button size="small" type="primary" icon={<HomeOutlined />}
                          href={`${window.location.origin}/office-search?focus=${selected.listing_id}`}
                          target="_blank" rel="noreferrer">
                          {t('inbox.viewProperty', 'View Property')}
                        </Button>
                      )}
                    </Space>
                  }
                  style={{ padding: '6px 12px', background: '#fffbe6', border: '1px solid #ffe58f' }}
                />
              </Col>
            )}

            {/* ── Tabs: Chat | Analysis | Properties ── */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Tabs
                activeKey={rightTab}
                onChange={setRightTab}
                size="small"
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                tabBarStyle={{ marginBottom: 0, paddingLeft: 4 }}
              >
                {/* ── CHAT TAB ── */}
                <TabPane tab={`💬 ${t('inbox.chatHistory','Chat History')}`} key="chat">
                  <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
                    {/* Customer header */}
                    <div style={{ padding: '8px 12px', background: '#f6f0ff', borderBottom: '1px solid #e0d0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar size={36} style={{ background: '#722ed1', flexShrink: 0 }}>
                        {(selected.customer?.name || '?')[0]}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <Text strong>{selected.customer?.name || t('inbox.customer','Customer')}</Text>
                        <div style={{ fontSize: 12 }}>
                          {selected.customer?.line_id && <Tag color="#00b900" style={{ fontSize: 11 }}>💬 {selected.customer.line_id}</Tag>}
                          {selected.customer?.whatsapp && <Tag color="#25d366" style={{ fontSize: 11 }}>📱 {selected.customer.whatsapp}</Tag>}
                          {selected.customer?.phone && <Tag style={{ fontSize: 11 }}>☎ {selected.customer.phone}</Tag>}
                          <Tag color={selected.customer?.tier === 'hot' ? 'red' : selected.customer?.tier === 'warm' ? 'orange' : 'blue'} style={{ fontSize: 11 }}>
                            {selected.customer?.tier === 'hot' ? '🔥 HOT' : selected.customer?.tier === 'warm' ? '⭐ WARM' : '❄️ COLD'}
                          </Tag>
                        </div>
                      </div>
                      <Text type="secondary" style={{ fontSize: 12 }}>{conversation?.messages?.length || 0} {t('inbox.messages','messages')}</Text>
                    </div>

                    {/* Messages */}
                    <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px', background: '#f9f9f9' }}>
                      {!conversation ? (
                        <div style={{ textAlign: 'center', marginTop: 40 }}><Spin tip={t('inbox.loading','Loading conversation...')} /></div>
                      ) : conversation.messages?.length === 0 ? (
                        <div style={{ textAlign: 'center', marginTop: 40, padding: '0 20px' }}>
                          <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
                          <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8 }}>
                            {t('inbox.startConv','Start conversation with')} {selected.customer?.name || t('inbox.customer','Customer')}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            {selected.customer?.line_id && `LINE: ${selected.customer.line_id}`}
                            {selected.customer?.whatsapp && ` · WhatsApp: ${selected.customer.whatsapp}`}
                          </Text>
                          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f6f0ff', borderRadius: 8, border: '1px solid #d3adf7', textAlign: 'left' }}>
                            <Text style={{ fontSize: 12, color: '#722ed1' }}>
                              {t('inbox.startHint', '💡 Type a message below. Click "AI Generate Reply" for an AI-written opener, then copy to Line / WhatsApp.')}
                            </Text>
                          </div>
                        </div>
                      ) : (
                        conversation.messages?.map((msg, i) => {
                          const isCustomer = msg.role === 'customer';
                          const srcIcon = { line: '💬', whatsapp: '📱', wechat: '💚', facebook: '👤', ddproperty: '🏢', manual: '✏️' }[msg.source] || '💬';
                          return (
                            <div key={msg.id} style={{ marginBottom: 16, display: 'flex', flexDirection: isCustomer ? 'row' : 'row-reverse', gap: 8, alignItems: 'flex-end' }}>
                              <Avatar size={32} style={{ background: isCustomer ? '#722ed1' : '#1890ff', flexShrink: 0 }}>
                                {isCustomer ? (selected.customer?.name || '?')[0] : 'JJ'}
                              </Avatar>
                              <div style={{ maxWidth: '72%' }}>
                                <div style={{
                                  padding: '10px 14px', borderRadius: isCustomer ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                                  background: isCustomer ? '#fff' : '#722ed1',
                                  color: isCustomer ? '#333' : '#fff',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                  fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                }}>
                                  {msg.content}
                                </div>
                                <div style={{ fontSize: 11, color: '#999', marginTop: 3, textAlign: isCustomer ? 'left' : 'right' }}>
                                  {srcIcon} {new Date(msg.timestamp).toLocaleString('zh-TW', { timeZone: 'Asia/Bangkok', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  {msg.status === 'sent' && !isCustomer && <Tag color="green" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px' }}>{t('inbox.sent','Sent')}</Tag>}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Compose box */}
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #e8e8e8', background: '#fff' }}>
                      {/* AI suggestion bar */}
                      {(selected.classification?.autoReply || selected.auto_reply) && !replyText && (
                        <div style={{ marginBottom: 8, padding: '6px 10px', background: '#f6f0ff', borderRadius: 6, fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text style={{ fontSize: 12, color: '#722ed1' }}>{t('inbox.aiSuggested','🤖 AI suggested reply ready')}</Text>
                          <Button size="small" type="link" style={{ padding: 0, color: '#722ed1' }}
                            onClick={() => setReplyText(selected.classification?.autoReply || selected.auto_reply || '')}>
                            {t('inbox.applySuggested','Apply Suggestion')}
                          </Button>
                        </div>
                      )}
                      <Input.TextArea
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        placeholder={selected._newCustomer
                          ? `${t('inbox.startConvPlaceholder','Start conversation')}... (Enter to send)`
                          : `${t('inbox.replyPlaceholder','Reply')}... (Shift+Enter new line, Enter to send)`
                        }
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
                        style={{ marginBottom: 8, fontSize: 14, borderRadius: 8 }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space>
                          <Button
                            size="small"
                            icon={<RobotOutlined />}
                            loading={generatingAI}
                            onClick={handleGenerateAIReply}
                            style={{ color: '#722ed1', borderColor: '#722ed1' }}
                          >
                            {t('inbox.generateBtn','AI Generate Reply')}
                          </Button>
                          <Button
                            size="small"
                            icon={<HomeOutlined />}
                            loading={generatingAI}
                            onClick={handleSerinaMatch}
                            style={{ color: '#e8363a', borderColor: '#e8363a' }}
                          >
                            🏠 Serina Match
                          </Button>
                          {replyText && (
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => { navigator.clipboard.writeText(replyText); message.success(t('inbox.copied','Copied!')); }}
                            >
                              {t('inbox.copy','Copy')}
                            </Button>
                          )}
                        </Space>
                        <Space>
                          {selected.customer?.line_id && replyText && (() => {
                            // Real Line User ID starts with 'U' + 32 hex chars
                            const isRealLineId = /^U[a-f0-9]{32}$/i.test(selected.customer.line_id);
                            return isRealLineId ? (
                            <Tooltip title={t('inbox.sendViaLineTooltip','Send directly via Line Bot')}>
                              <Button
                                size="small"
                                style={{ background: '#00b900', color: '#fff', border: 'none' }}
                                loading={sendingReply}
                                onClick={async () => {
                                  setSendingReply(true);
                                  try {
                                    const res = await fetch(`${API}/customers/${selected.customer.id}/line-push`, {
                                      method: 'POST',
                                      headers: h(),
                                      body: JSON.stringify({ text: replyText }),
                                    });
                                    const data = await res.json();
                                    if (data.ok) {
                                      message.success(t('inbox.lineSent','✅ Sent via Line Bot!'));
                                      setReplyText('');
                                      loadConversation(selected.customer?.id);
                                      loadInquiries(true);
                                    } else {
                                      // Fallback to copy if Line push fails
                                      navigator.clipboard.writeText(replyText);
                                      message.warning(t('inbox.lineFailed', 'Line send failed') + ` (${data.detail || '?'}) ` + t('inbox.copiedToClipboard', 'Copied to clipboard'));
                                    }
                                  } catch (e) {
                                    navigator.clipboard.writeText(replyText);
                                    message.warning(t('inbox.connectionFailed','Connection failed, copied to clipboard'));
                                  } finally {
                                    setSendingReply(false);
                                  }
                                }}
                              >
                                {t('inbox.sendViaLine','💬 Send via Line Bot')}
                              </Button>
                            </Tooltip>
                            ) : (
                            // Display name (@handle) — can only copy
                            <Tooltip title={t('inbox.lineIdHint','Line ID (customer must message first to get real User ID)')}>
                              <Button size="small" style={{ background: '#00b900', color: '#fff', border: 'none' }}
                                onClick={() => { navigator.clipboard.writeText(replyText); message.success(t('inbox.copied','Copied!')); }}>
                                {t('inbox.copyLineManual','💬 Copy (Line)')}
                              </Button>
                            </Tooltip>
                            );
                          })()}
                          {selected.customer?.whatsapp && replyText && (
                            <Tooltip title={t('inbox.sendViaWhatsApp','Send via WhatsApp')}>
                              <Button size="small" style={{ background: '#25d366', color: '#fff', border: 'none' }}
                                loading={sendingReply}
                                onClick={async () => {
                                  setSendingReply(true);
                                  try {
                                    const res = await fetch(`${API}/customers/${selected.customer.id}/send-whatsapp`, {
                                      method: 'POST', headers: h(),
                                      body: JSON.stringify({ message: replyText }),
                                    });
                                    const data = await res.json();
                                    if (data.ok) {
                                      message.success(t('inbox.whatsappSent','✅ Sent via WhatsApp!'));
                                      setReplyText('');
                                      loadConversation(selected.customer?.id);
                                      loadInquiries(true);
                                    } else {
                                      navigator.clipboard.writeText(replyText);
                                      message.info(t('inbox.whatsappNotConfigured','WhatsApp not configured — copied to clipboard'));
                                    }
                                  } catch (e) {
                                    navigator.clipboard.writeText(replyText);
                                    message.info(t('inbox.whatsappNotConfigured','WhatsApp not configured — copied to clipboard'));
                                  } finally {
                                    setSendingReply(false);
                                  }
                                }}
                              >
                                {t('inbox.sendViaWhatsappBtn','📱 Send WhatsApp')}
                              </Button>
                            </Tooltip>
                          )}
                          <Button
                            type="primary"
                            size="small"
                            icon={<SendOutlined />}
                            disabled={!replyText.trim()}
                            loading={sendingReply}
                            onClick={handleSendReply}
                            style={{ background: '#722ed1', borderColor: '#722ed1' }}
                          >
                            {t('inbox.saveRecord','Save Record')}
                          </Button>
                        </Space>
                      </div>
                    </div>
                  </div>
                </TabPane>

                {/* ── ANALYSIS TAB ── */}
                <TabPane tab={t('inbox.aiAnalysis','🤖 AI Analysis')} key="analysis">
                  <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', padding: '4px 0' }}>
                    <Row gutter={20}>
                    {/* Classification Result */}
                    <Col span={12}>
                      <Card title={t('inbox.aiAnalysisResult','🤖 AI Analysis')} size="small" style={{ marginBottom: 16 }}>
                {/* Lead Score */}
                <LeadScoreBar
                  score={selected.classification?.leadScore || selected.customer?.lead_score || 0}
                  breakdown={selected.classification?.scoreBreakdown}
                />
                <Divider style={{ margin: '12px 0' }} />
                {/* Classification details */}
                <Row gutter={8}>
                  {[
                    { label: t('inbox.intent','Intent'), value: selected.classification?.intent || selected.customer?.intent, icon: '🎯' },
                    { label: t('inbox.language','Language'), value: selected.classification?.language || selected.customer?.language, icon: '🌐' },
                    { label: t('inbox.urgency','Urgency'), value: selected.classification?.urgency || selected.customer?.urgency, icon: '⚡' },
                    { label: t('inbox.area','Area'), value: selected.classification?.area || selected.customer?.preferred_area, icon: '📍' },
                  ].map((item, i) => (
                    <Col span={12} key={i} style={{ marginBottom: 6 }}>
                      <Text type="secondary" style={{ fontSize: 11 }}>{item.icon} {item.label}</Text>
                      <div><Tag style={{ fontSize: 12 }}>{item.value || t('inbox.unknown','Unknown')}</Tag></div>
                    </Col>
                  ))}
                </Row>
                {/* Budget */}
                {(selected.classification?.budget?.max || selected.customer?.budget_max) && (
                  <Alert
                    message={<span>💰 {t('inbox.budget', 'Budget')}: THB {(selected.classification?.budget?.min || 0).toLocaleString()} – {(selected.classification?.budget?.max || selected.customer?.budget_max || 0).toLocaleString()}/{t('inbox.month', 'month')}</span>}
                    type="info" showIcon={false} style={{ padding: '4px 10px', marginTop: 8 }}
                  />
                )}
              </Card>

              {/* Auto-Reply */}
              <Card
                title={t('inbox.autoReply','💬 AI Auto-Reply')}
                size="small"
                extra={
                  <Button
                    size="small"
                    icon={copySuccess ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CopyOutlined />}
                    onClick={() => handleCopyReply(selected.classification?.autoReply || selected.auto_reply || '')}
                  >
                    {copySuccess ? t('inbox.copied','Copied') : t('inbox.copy','Copy')}
                  </Button>
                }
              >
                <Paragraph style={{ fontSize: 13, background: '#f6f0ff', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                  {selected.classification?.autoReply || selected.auto_reply || t('inbox.noAutoReply','No auto-reply')}
                </Paragraph>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <Button size="small" style={{ color: '#25d366', borderColor: '#25d366' }} icon={<WhatsAppOutlined />}>
                    WhatsApp
                  </Button>
                  <Button size="small" style={{ color: '#00b900', borderColor: '#00b900' }}>
                    💬 Line
                  </Button>
                  {selected.id && selected.status !== 'replied' && (
                    <Button size="small" icon={<CheckCircleOutlined />} onClick={() => handleMarkReplied(selected.id)}>
                      {t('inbox.markReplied','Mark Replied')}
                    </Button>
                  )}
                </div>
              </Card>

              {/* Suggested Action */}
              {(selected.classification?.suggestedAction) && (
                <Alert
                  style={{ marginTop: 12 }}
                  message={<span>💡 {t('inbox.suggestedAction','Suggested Action')}: {selected.classification.suggestedAction}</span>}
                  type="warning"
                />
              )}

              {/* Contact Info */}
              {selected.customer && (
                <Card title={t('inbox.customerInfo','👤 Customer Info')} size="small" style={{ marginTop: 12 }}>
                  {[
                    { icon: '📱', label: 'Phone', value: selected.customer.phone },
                    { icon: '💬', label: 'Line', value: selected.customer.line_id },
                    { icon: '📱', label: 'WhatsApp', value: selected.customer.whatsapp },
                    { icon: '💚', label: 'WeChat', value: selected.customer.wechat_id },
                    { icon: '📧', label: 'Email', value: selected.customer.email },
                  ].filter(x => x.value).map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f5f5f5' }}>
                      <Text style={{ fontSize: 13 }}>{item.icon} {item.label}</Text>
                      <Space>
                        <Text code style={{ fontSize: 12 }}>{item.value}</Text>
                        <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(item.value)} />
                      </Space>
                    </div>
                  ))}
                </Card>
              )}
            </Col>

            {/* Matched Properties */}
            <Col span={12}>
              <Card
                title={
                  <Space>
                    <span>🏠 {t('inbox.matchedProperties','Matched Properties')}</span>
                    <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>
                      {t('inbox.houseaiSource','HouseAI · Auto-matched to requirements')}
                    </span>
                    {loadingProps && <Spin size="small" />}
                  </Space>
                }
                size="small"
                style={{ marginBottom: 16 }}
              >
                {loadingProps ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Spin tip={t('inbox.loadingProps','Loading from HouseAI...')} />
                  </div>
                ) : (selected.matched_properties || []).length === 0 ? (
                  <div style={{ padding: '12px 0', textAlign: 'center' }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {selected.customer?.preferred_area
                        ? `${t('inbox.noMatchForArea','No properties for')} ${selected.customer.preferred_area}`
                        : t('inbox.clickToLoad','Click an inquiry to load matched properties')}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('inbox.budget', 'Budget')}: {selected.customer?.budget_max ? `THB ${selected.customer.budget_max.toLocaleString()}` : t('inbox.unknown', 'Unknown')} ·
                      {t('inbox.area','Area')}: {selected.customer?.preferred_area || t('inbox.notSet','Not set')}
                    </Text>
                  </div>
                ) : (
                  (selected.matched_properties || []).map((prop, i) => (
                    <PropertyCard key={i} prop={prop}
                      onSchedule={handleScheduleViewing}
                      onSend={(p) => {
                        const parsed = p.parsed || {};
                        const price = parsed.price || {};
                        const loc = parsed.location || {};
                        const text = `🏠 ${parsed.title || 'Condo'}\n💰 ${price.amount?.toLocaleString()} THB/${price.period === 'monthly' ? 'month' : price.period}\n📍 ${loc.area}, near ${loc.nearBTS} BTS\n🛏 ${parsed.bedrooms || '?'} bed / ${parsed.bathrooms || '?'} bath\n\nInterested? Let me arrange a viewing! 😊`;
                        navigator.clipboard.writeText(text);
                        message.success(t('inbox.propCopied','Property info copied!'));
                      }} />
                  ))
                )
                }
              </Card>

              {/* Schedule Viewing */}
              <Card title={t('inbox.scheduleViewing','📅 Schedule Viewing')} size="small">
                {selected.customer ? (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('inbox.arrangeFor','Arrange viewing for')} {selected.customer?.name || t('inbox.thisCustomer','this customer')}</Text>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <Input {...{placeholder: t('inbox.propertyName','Property name')}} size="small" id="vt" />
                      <Button
                        size="small" type="primary"
                        onClick={async () => {
                          const title = document.getElementById('vt')?.value;
                          await fetch(`${API}/viewings`, {
                            method: 'POST', headers: h(),
                            body: JSON.stringify({ customer_id: selected.customer.id, listing_title: title }),
                          });
                          message.success(t('inbox.viewingArranged','Viewing arranged!'));
                          loadCustomerViewings(selected.customer.id);
                        }}
                      >
                        {t('inbox.arrange','Arrange')}
                      </Button>
                    </div>
                  </div>
                ) : <Text type="secondary">{t('inbox.classifyFirst','Complete inquiry analysis before scheduling a viewing')}</Text>}
              </Card>

              {/* Viewing History */}
              <Card
                title={<span>📋 {t('inbox.viewingHistory','Viewing History')}{(customerViewings.length > 0 ? ` (${customerViewings.length})` : '')}</span>}
                size="small"
                style={{ marginTop: 12 }}
              >
                {customerViewings.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>{t('inbox.viewingNoHistory','No viewing history yet')}</Text>
                ) : (
                  customerViewings.map(v => {
                    const isPast = v.scheduled_at && new Date(v.scheduled_at) < new Date();
                    const needsResult = isPast && !v.result;
                    const resultColors = { yes: 'green', no: 'red', rescheduled: 'orange', cancelled: 'default' };
                    const resultLabels = {
                      yes: '✅ ' + t('inbox.viewingResultYes','Satisfied'),
                      no: '❌ ' + t('inbox.viewingResultNo','Not Satisfied'),
                      rescheduled: '🔄 ' + t('inbox.viewingResultRescheduled','Rescheduled'),
                      cancelled: '🚫 ' + t('inbox.viewingResultCancelled','Cancelled'),
                    };
                    return (
                      <div key={v.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <Text strong style={{ fontSize: 13 }}>{v.listing_title}</Text>
                            <div>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                📅 {v.scheduled_at ? new Date(v.scheduled_at).toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBC'}
                              </Text>
                            </div>
                          </div>
                          <div style={{ flexShrink: 0 }}>
                            {v.result ? (
                              <Tag color={resultColors[v.result]}>{resultLabels[v.result] || v.result}</Tag>
                            ) : needsResult ? (
                              <Space size={4} wrap>
                                <Button
                                  size="small"
                                  type="primary"
                                  style={{ background: '#52c41a', borderColor: '#52c41a', fontSize: 11 }}
                                  loading={updatingViewing === v.id}
                                  onClick={() => handleUpdateViewingResult(v.id, 'yes')}
                                >
                                  {t('inbox.viewingResultYes','Yes')}
                                </Button>
                                <Button
                                  size="small"
                                  danger
                                  style={{ fontSize: 11 }}
                                  loading={updatingViewing === v.id}
                                  onClick={() => handleUpdateViewingResult(v.id, 'no')}
                                >
                                  {t('inbox.viewingResultNo','No')}
                                </Button>
                                <Button
                                  size="small"
                                  style={{ fontSize: 11 }}
                                  loading={updatingViewing === v.id}
                                  onClick={() => handleUpdateViewingResult(v.id, 'rescheduled')}
                                >
                                  {t('inbox.viewingResultRescheduled','Rescheduled')}
                                </Button>
                                <Button
                                  size="small"
                                  style={{ fontSize: 11 }}
                                  loading={updatingViewing === v.id}
                                  onClick={() => handleUpdateViewingResult(v.id, 'cancelled')}
                                >
                                  {t('inbox.viewingResultCancelled','Cancel')}
                                </Button>
                              </Space>
                            ) : (
                              <Tag color="blue">{t('inbox.viewingUpcoming','Upcoming')}</Tag>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </Card>
            </Col>
                    </Row>
                  </div>
                </TabPane>

                {/* ── PROPERTIES TAB ── */}
                <TabPane tab={<span>🏠 {t('inbox.matchedProperties','Matched Properties')}{(selected?.matched_properties||[]).length > 0 ? ` (${selected.matched_properties.length})` : ''}</span>} key="properties">
                  <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)', padding: '8px 0' }}>
                    {loadingProps ? (
                      <div style={{ textAlign: 'center', padding: '30px 0' }}><Spin {...{tip: t('inbox.loadingProps','Loading from HouseAI...')}} /></div>
                    ) : (selected.matched_properties || []).length === 0 ? (
                      <Empty description={t('inbox.noProperties','No matched properties')} style={{ padding: '30px 0' }} />
                    ) : (
                      (selected.matched_properties || []).map((prop, i) => (
                        <PropertyCard key={i} prop={prop}
                          onSchedule={handleScheduleViewing}
                          onSend={(p) => {
                            const parsed = p.parsed || {};
                            const price = parsed.price || {};
                            const loc = parsed.location || {};
                            const text = `🏠 ${parsed.title || 'Condo'}\n💰 ${price.amount?.toLocaleString()} THB/${price.period === 'monthly' ? 'month' : price.period}\n📍 ${loc.area}, near ${loc.nearBTS} BTS\n🛏 ${parsed.bedrooms || '?'} bed / ${parsed.bathrooms || '?'} bath\n\nInterested? Let me arrange a viewing! 😊`;
                            setReplyText(text);
                            setRightTab('chat');
                            message.success(t('inbox.propAddedToChat','Property added to chat box, confirm and send!'));
                          }} />
                      ))
                    )}
                  </div>
                </TabPane>

                {/* ── AD POSTS TAB ── */}
                <TabPane tab={<span>📊 {t('adPosts.tabName','Ad Performance')}</span>} key="ads">
                  <AdPostsPanel />
                </TabPane>
              </Tabs>
            </div>
          </div>
        )}
      </Content>

      {/* ── Schedule Viewing Modal ── */}
      <Modal
        title={<Space><CalendarOutlined style={{ color: '#52c41a' }} /> {t('inbox.scheduleViewing','📅 Schedule Viewing')}</Space>}
        open={!!scheduleModal}
        onOk={confirmScheduleViewing}
        onCancel={() => setScheduleModal(null)}
        okText={t('inbox.arrange','Arrange')}
        cancelText={t('common.cancel','Cancel')}
        okButtonProps={{ style: { background: '#52c41a', border: 'none' } }}
      >
        {scheduleModal && (
          <div>
            <div style={{ marginBottom: 12, padding: '10px 12px', background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
              <Text strong style={{ fontSize: 13 }}>🏠 {scheduleModal.parsed?.title || 'Property'}</Text>
              {scheduleModal.parsed?.price?.amount && (
                <div style={{ fontSize: 12, color: '#52c41a', marginTop: 2 }}>
                  ฿{scheduleModal.parsed.price.amount.toLocaleString()} / {scheduleModal.parsed.price.period || 'mo'}
                </div>
              )}
              {scheduleModal.parsed?.location?.area && (
                <div style={{ fontSize: 12, color: '#666' }}>📍 {scheduleModal.parsed.location.area}</div>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>{t('inbox.propertyName','Property name')} ({t('common.edit','editable')})</Text>
              <Input
                value={schedulingProp}
                onChange={e => setSchedulingProp(e.target.value)}
                placeholder={scheduleModal.parsed?.title || 'Property name'}
              />
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>📅 {t('inbox.viewingDate','Viewing date & time')} ({t('inbox.optional','optional')})</Text>
              <Input
                type="datetime-local"
                value={scheduleDate}
                onChange={e => setScheduleDate(e.target.value)}
              />
            </div>
            {selected?.customer?.name && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                {t('inbox.arrangeFor','Arrange viewing for')} <Text strong>{selected.customer.name}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
}
